"use client";

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import type { ReadonlyURLSearchParams } from "next/navigation";
import { X, Search, ChevronDown, Check, Info } from "lucide-react";
import { useT, useLocale } from "@/lib/locale-context";
import type { TranslationKey } from "@/lib/i18n";
import { getDevelopers, getFeatureTags, type DeveloperRow, type FeatureTagVocab } from "@/lib/api";
import { haptic } from "@/lib/haptics";
import { useSheetDrag } from "@/lib/use-sheet-drag";
import ListingsLocationPicker from "./ListingsLocationPicker";
import CreateAlertButton from "./CreateAlertButton";
import DeveloperLogo from "@/components/developers/DeveloperLogo";
import { AlertSwitch } from "@/components/alerts/AlertPanel";
import { apiParamsFromSearch } from "@/lib/listing-search-params";
import { showLabel } from "@/lib/listings-count";
import { FACT_HINT, FACT_LABEL, FACT_SWITCHES, MERGED_FACTS, topicsFor, type FactFilter, type FactTopic } from "@/lib/text-facts";
import { AMENITY_ICON, FACT_ICON, ICON, KIND_ICON, type LucideIcon } from "./filter-icons";
import { useBackToClose } from "@/lib/use-back-to-close";
import { isPhoneWidth, prefersReducedMotion } from "@/lib/motion";

const PANEL_MS = 220;
import { foldKeepIndex } from "@/lib/search-terms";
import { trackEvent } from "@/lib/tracking";
import { useViewed } from "@/lib/viewed";
import { sourceSummary } from "@/lib/source-summary";
import { countActiveFilters } from "@/lib/active-filter-count";
import { isSeparate, officesOnly, kindChipKey, kindsOf, LISTING_KINDS, type ListingKind } from "@/lib/property-kinds";
import { FILTER_COUNT, ROW_ON, SEGMENT_OFF, SEGMENT_ON, SELECTED, UNSELECTED } from "@/lib/ui-classes";

// NOTE: This is the full advanced-search dialog ("Ətraflı axtarış") opened
// from the quick filter bar's "Filtrlər" button. It owns the complete draft
// filter set and commits it in one batch on apply (the hybrid model: the
// bar's quick chips apply instantly, this modal batches).

// Default radius (km) used when the user enables the radius filter but
// hasn't moved the slider yet.
const DEFAULT_RADIUS_KM = 2;

/** < sm (639px): the panel is a full-screen sheet, each label above its choices. */
function useIsMobileWidth(): boolean {
  const [m, setM] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    setM(mq.matches);
    const h = (e: MediaQueryListEvent) => setM(e.matches);
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, []);
  return m;
}

/** A loan rate as typed: up to two digits, one dot, one decimal ("7.5"). */
function rateInput(raw: string): string {
  const m = raw.replace(",", ".").replace(/[^\d.]/g, "").match(/^\d{0,2}(?:\.\d?)?/);
  return m ? m[0] : "";
}

interface Props {
  /** Whether the modal is open. */
  open: boolean;
  /** Close the modal without applying. */
  onClose: () => void;
  searchParams: ReadonlyURLSearchParams;
  /** Commit the whole draft to the URL at once + refetch. Called only
   *  when the user clicks "Axtar". */
  onSearch: (params: Record<string, string | string[]>) => void;
  /** Clear all filters (URL + draft). */
  onReset: () => void;
  /** Optional extra footer controls, rendered as a row above Reset / Apply. */
  footerExtras?: React.ReactNode;
  /** Optional compact actions in the header beside the close button
   *  (the map's Saved-filters + Create-alert icons): out of the footer,
   *  where on a phone they took a full row of screen. */
  headerExtras?: React.ReactNode;
  /** Show the kind (flat, house, land) and the build at the top: the map
   *  has no bar pill for it on phones (owner, 2026-10-12). */
  showKind?: boolean;
  /** The alert form: no listed-date or Viewed filters (an alert always
   *  sends what is new) and the button applies instead of searching. */
  forAlert?: boolean;
  /** The developers' offers page and its alert (owner, 2026-10-21: "the
   *  same filters as everywhere else", the exact developer and residence
   *  easy to pick): the developers and residences that have offers lead
   *  the sheet, the choices every offer shares (sale, the sites, the
   *  seller) leave it, and the live count is the offers'. */
  offers?: OffersScope;
}

export interface OffersScope {
  developers: DeveloperRow[];
  residences: { cluster_id: string; name: string; n: number }[];
  /** How many offers the draft finds (null: unknown). */
  count: (params: Record<string, string | string[]>) => Promise<number | null>;
}

// Only apartment + house are supported - the only categories the AI model
// scores and the only categories the indexer ingests. Office/garage/land/
// commercial are intentionally not offered.
const ROOMS = ["1", "2", "3", "4", "5"];
const DEAL_TIERS = [
  { value: "great",     key: "listings.filter.deal_tier.great" as const },
  { value: "good",      key: "listings.filter.deal_tier.good" as const },
  { value: "fair",      key: "listings.filter.deal_tier.fair" as const },
  { value: "overpriced",key: "listings.filter.deal_tier.overpriced" as const },
];

// Photo-intel renovation tiers: the vision model's read of the rooms.
const PHOTO_TIERS = ["luxury", "modern", "mid", "dated", "shell"] as const;

// What the gallery itself shows (photo_scenes), AND-combined: a room
// type, any room inside, a floor plan. The sea view and a pool are
// amenity tags, which photos confirm too.
const PHOTO_HAS = ["bathroom", "kitchen", "interior", "floor_plan"] as const;

// The amenity tags in rows by what they describe. The backend's
// "building" category holds a building's amenities and the Soviet-era
// series alike: they split here by id, and penthouse and duplex join the
// plan; furnished (the backend files it under the deal) reads as comfort.
type AmenityRow = "view" | "comfort" | "building" | "condition" | "layout" | "series" | "deal" | "rent_terms";
const AMENITY_ROWS: AmenityRow[] = ["view", "comfort", "building", "condition", "layout", "series", "deal", "rent_terms"];
const SERIES_TAGS = new Set([
  "leningrad_project", "stalinka", "khrushchyovka", "kyiv_project", "minsk_project", "french_project", "german_project", "experimental_project",
]);
const PLAN_TAGS = new Set(["penthouse", "duplex"]);
function amenityRow(id: string, category: string): AmenityRow {
  if (SERIES_TAGS.has(id)) return "series";
  if (PLAN_TAGS.has(id)) return "layout";
  if (id === "furnished") return "comfort";
  return category as AmenityRow;
}
// Tags the photos confirm as well as the text (backend
// listing_filters.PHOTO_TAG_EVIDENCE: pool = feat:pool, sea_view =
// view:sea): first in their row (no mark on the chip since 2026-10-29).
const PHOTO_CONFIRMED = new Set(["pool", "sea_view"]);
// The tags people filter by most (tracking "listings.filters", 30 days to
// 2026-10-27: sea view 24, furnished 18, panoramic 11, urgent 9 people):
// first in their rows, ahead of the vocabulary's own popular list.
const USED_FIRST = ["sea_view", "furnished", "panoramic", "urgent"];
// Chips a row shows before its "+N" (one line at 390 px with three).
const ROW_VISIBLE = 4;
const ROW_VISIBLE_PHONE = 3;

// The sites the index reads (listing_index.source), for the "Mənbə" group.
const LISTING_SOURCES = [
  { id: "bina_az", label: "bina.az" },
  { id: "tap_az", label: "tap.az" },
  { id: "lalafo_az", label: "lalafo.az" },
  { id: "arenda_az", label: "arenda.az" },
  { id: "yeniemlak_az", label: "yeniemlak.az" },
  { id: "emlaktap_az", label: "emlaktap.az" },
  { id: "ev10_az", label: "ev10.az" },
];

// The full draft filter shape the panel edits locally. Single-value
// fields are strings (""=unset); multi-value fields are arrays.
interface Draft {
  leased: string;          // "true" | "false" | ""
  viewed: string;          // "hide" | "only" | "" (lib/viewed)
  sources: string[];       // listing_index.source ids; none = every site
  category: string;
  // Commercial premises and garages: the kinds picked (property_kind).
  propertyKinds: string[];
  buildingType: string;    // "new" | "old" | ""
  // Bar-only merged listing-type (new/old/house); carried with no UI here
  // so an advanced-modal apply doesn't wipe what the bar set.
  listingKind: string[];
  priceMin: string;
  priceMax: string;
  rooms: string[];
  areaMin: string;
  sotMin: string;
  sotMax: string;
  ppsMin: string;
  ppsMax: string;
  // The price per m² (shops and garages read it first).
  ppmMin: string;
  ppmMax: string;
  areaMax: string;
  floorMin: string;
  floorMax: string;
  excludeFirstFloor: boolean;  // "not first/ground floor"
  excludeLastFloor: boolean;   // "not top floor"
  // The number of floors (ticket 63): a flat's building, a house's own
  // storeys (total_floors_min / total_floors_max).
  totalFloorsMin: string;
  totalFloorsMax: string;
  hasRepair: string;       // "yes" | "no" | ""
  // Photo-intel renovation tiers (luxury|modern|mid|dated|shell) -
  // multi-select over the vision model's photo_condition tag.
  photoConditions: string[];
  // What the photos show (PHOTO_HAS values), all of them required.
  photoHas: string[];
  featureTags: string[];
  hasMortgage: string;
  mortgageKind: string;    // "" | "takeover": sold with its loan
  maxInitial: string;      // AZN cap on the stated initial payment
  mortgageRateMin: string; // the loan's rate range in percent, take-overs only
  mortgageRateMax: string;
  mortgageTermsKnown: string; // "" | "yes" | "no": the text states the terms (take-overs only)
  mortgageYears: string[]; // years-left bands ("le5", "6-10", "11-15", "gt15"), take-overs only
  listedAfter: string;     // "Listed since": Baku calendar day, YYYY-MM-DD or ""
  listedBefore: string;    // ... up to and including this day
  hasBillOfSale: string;
  deedGap: string;         // "yes" (the deed holds less) | "no" | ""
  minYield: string;        // "5" | "7" | "9" | "12" | "" (gross rental yield floor, sale flats)
  sellerType: string;
  locationIds: string[];
  dealTiers: string[];
  // Residence (complex) multi-select - filters by the deterministic
  // matcher's complex_slug tag on listing_index rows.
  complexSlugs: string[];
  // Developer (construction company) slugs: every listing in their
  // verified residences (backend services/developers).
  developers: string[];
  // Streets (services/streets ids), picked in the location picker's search.
  streets: string[];
  // Towns outside Baku (?city=), set from the search bar: the sheet has
  // no control for them and carries them through, so applying it keeps
  // the town and its count includes it.
  cities: string[];
  // The areas drawn on the map (?draw=): the sheet has no control for
  // them either and hands them back unchanged, so applying it keeps the
  // drawn area and its count stays inside it (ticket 68).
  draw: string;
  // Rent period ("" = monthly, the default; daily | room | all) and the
  // walk to the metro in minutes ("" = any).
  rentPeriod: string;
  metroWalk: string;
  // Radius (map-circle) filter. centerLat/centerLng are "" when unset;
  // radiusKm is always a number (the slider has a floor).
  centerLat: string;
  centerLng: string;
  radiusKm: number;
  // What the listing's own text says (lib/text-facts): the facts picked
  // under the deed and in the sheet's topics, AND-combined.
  facts: string[];
}

const EMPTY_DRAFT: Draft = {
  leased: "", viewed: "", sources: [], category: "", propertyKinds: [], buildingType: "", listingKind: [], priceMin: "", priceMax: "",
  rooms: [], areaMin: "", areaMax: "", sotMin: "", sotMax: "", ppsMin: "", ppsMax: "", ppmMin: "", ppmMax: "", floorMin: "", floorMax: "",
  excludeFirstFloor: false, excludeLastFloor: false, totalFloorsMin: "", totalFloorsMax: "",
  hasRepair: "", photoConditions: [], photoHas: [], featureTags: [], hasMortgage: "", mortgageKind: "", maxInitial: "", mortgageRateMin: "", mortgageRateMax: "", mortgageTermsKnown: "", mortgageYears: [], listedAfter: "", listedBefore: "", hasBillOfSale: "", deedGap: "", minYield: "", sellerType: "",
  locationIds: [], dealTiers: [], complexSlugs: [], developers: [], streets: [], cities: [], draw: "", rentPeriod: "", metroWalk: "",
  centerLat: "", centerLng: "", radiusKm: DEFAULT_RADIUS_KM, facts: [],
};

/** Build the initial draft from the committed URL params. */
function draftFromParams(sp: ReadonlyURLSearchParams): Draft {
  return {
    leased: sp.get("leased") ?? "",
    viewed: sp.get("viewed") ?? "",
    sources: sp.getAll("source"),
    category: sp.get("category") ?? "",
    propertyKinds: sp.getAll("property_kind"),
    buildingType: sp.get("building_type") ?? "",
    listingKind: sp.getAll("listing_kind"),
    priceMin: sp.get("price_min") ?? "",
    priceMax: sp.get("price_max") ?? "",
    rooms: sp.getAll("rooms"),
    areaMin: sp.get("area_min") ?? "",
    sotMin: sp.get("sot_min") ?? "",
    sotMax: sp.get("sot_max") ?? "",
    ppsMin: sp.get("pps_min") ?? "",
    ppsMax: sp.get("pps_max") ?? "",
    ppmMin: sp.get("ppm_min") ?? "",
    ppmMax: sp.get("ppm_max") ?? "",
    areaMax: sp.get("area_max") ?? "",
    floorMin: sp.get("floor_min") ?? "",
    floorMax: sp.get("floor_max") ?? "",
    excludeFirstFloor: sp.get("exclude_first_floor") === "true",
    excludeLastFloor: sp.get("exclude_last_floor") === "true",
    totalFloorsMin: sp.get("total_floors_min") ?? "",
    totalFloorsMax: sp.get("total_floors_max") ?? "",
    // The repair and the mortgage are one question each: an old link's
    // fact value is the question's side (lib/text-facts MERGED_FACTS).
    hasRepair: sp.get("has_repair") ?? (sp.getAll("fact").includes("ready") ? "yes" : sp.getAll("fact").includes("needs_work") ? "no" : ""),
    photoConditions: sp.getAll("photo_condition"),
    photoHas: sp.getAll("photo_has"),
    featureTags: sp.getAll("tags"),
    hasMortgage: sp.get("has_mortgage") ?? (sp.getAll("fact").includes("mortgage") ? "yes" : ""),
    mortgageKind: sp.get("mortgage_kind") ?? "",
    maxInitial: sp.get("max_initial") ?? "",
    mortgageRateMin: rateInput(sp.get("mortgage_rate_min") ?? ""),
    mortgageRateMax: rateInput(sp.get("mortgage_rate_max") ?? ""),
    mortgageTermsKnown: sp.get("mortgage_terms_known") ?? "",
    mortgageYears: sp.getAll("mortgage_years"),
    listedAfter: sp.get("listed_after") ?? "",
    listedBefore: sp.get("listed_before") ?? "",
    hasBillOfSale: sp.get("has_bill_of_sale") ?? "",
    deedGap: sp.get("deed_gap") ?? "",
    minYield: sp.get("min_yield") ?? "",
    sellerType: sp.get("seller_type") ?? "",
    locationIds: sp.getAll("location_ids"),
    dealTiers: sp.getAll("deal_tier"),
    complexSlugs: sp.getAll("complex_slug"),
    developers: sp.getAll("developer"),
    streets: sp.getAll("street"),
    cities: sp.getAll("city"),
    draw: sp.get("draw") ?? "",
    rentPeriod: sp.get("rent_period") ?? "",
    metroWalk: sp.get("metro_walk") ?? "",
    centerLat: sp.get("center_lat") ?? "",
    centerLng: sp.get("center_lng") ?? "",
    radiusKm: Number(sp.get("radius_km")) || DEFAULT_RADIUS_KM,
    facts: sp.getAll("fact").filter((f) => !(f in MERGED_FACTS)),
  };
}

/** Serialize a draft into the query-param map onSearch expects. Empty
 *  values are dropped so the URL stays clean. */
function draftToParams(d0: Draft): Record<string, string | string[]> {
  // A reversed range (min above max) is a typo, not a request for an
  // empty page: swap each pair before serialising.
  const d = { ...d0 };
  // Floors are whole numbers: the API takes integers, and a "2.5" typed
  // into a box would fail the whole search.
  for (const k of ["floorMin", "floorMax", "totalFloorsMin", "totalFloorsMax"] as const) {
    if (d[k] === "") continue;
    const n = Math.round(Number(d[k]));
    d[k] = Number.isFinite(n) && n >= 0 ? String(n) : "";
  }
  type Lo = "priceMin" | "areaMin" | "floorMin" | "ppmMin" | "totalFloorsMin";
  type Hi = "priceMax" | "areaMax" | "floorMax" | "ppmMax" | "totalFloorsMax";
  const swap = (lo: Lo, hi: Hi) => {
    if (d[lo] && d[hi] && Number(d[lo]) > Number(d[hi])) [d[lo], d[hi]] = [d[hi], d[lo]];
  };
  swap("priceMin", "priceMax");
  swap("areaMin", "areaMax");
  swap("floorMin", "floorMax");
  swap("ppmMin", "ppmMax");
  swap("totalFloorsMin", "totalFloorsMax");
  const params: Record<string, string | string[]> = {};
  if (d.leased) params.leased = d.leased;
  if (d.viewed) params.viewed = d.viewed;
  if (d.sources.length) params.source = d.sources;
  if (d.category) params.category = d.category;
  if (d.propertyKinds.length && isSeparate(d.category)) params.property_kind = d.propertyKinds;
  if (d.buildingType) params.building_type = d.buildingType;
  if (d.listingKind.length) params.listing_kind = d.listingKind;
  if (d.priceMin) params.price_min = d.priceMin;
  if (d.priceMax) params.price_max = d.priceMax;
  // Rooms apply to homes and to offices alone; a stale pick from an office
  // search must not narrow shops or garages it no longer shows.
  if (d.rooms.length && (!isSeparate(d.category) || officesOnly(d.category, d.propertyKinds))) params.rooms = d.rooms;
  if (d.areaMin) params.area_min = d.areaMin;
  if (d.sotMin) params.sot_min = d.sotMin;
  if (d.sotMax) params.sot_max = d.sotMax;
  if (d.ppsMin) params.pps_min = d.ppsMin;
  if (d.ppsMax) params.pps_max = d.ppsMax;
  if (d.ppmMin) params.ppm_min = d.ppmMin;
  if (d.ppmMax) params.ppm_max = d.ppmMax;
  if (d.areaMax) params.area_max = d.areaMax;
  if (d.floorMin) params.floor_min = d.floorMin;
  if (d.floorMax) params.floor_max = d.floorMax;
  if (d.excludeFirstFloor) params.exclude_first_floor = "true";
  if (d.excludeLastFloor) params.exclude_last_floor = "true";
  // The number of floors is a home's: none for a plot, a shop or a garage
  // (mirrors the bar's kindChangePatch), so a stale one never empties them.
  if (isHome(d)) {
    if (d.totalFloorsMin) params.total_floors_min = d.totalFloorsMin;
    if (d.totalFloorsMax) params.total_floors_max = d.totalFloorsMax;
  }
  if (d.hasRepair) params.has_repair = d.hasRepair;
  if (d.photoConditions.length) params.photo_condition = d.photoConditions;
  if (d.photoHas.length) params.photo_has = d.photoHas;
  if (d.featureTags.length) params.tags = d.featureTags;
  if (d.hasMortgage) params.has_mortgage = d.hasMortgage;
  if (d.mortgageKind) {
    params.mortgage_kind = d.mortgageKind;
    if (d.maxInitial) params.max_initial = d.maxInitial;
    if (d.mortgageRateMin) params.mortgage_rate_min = d.mortgageRateMin;
    if (d.mortgageRateMax) params.mortgage_rate_max = d.mortgageRateMax;
    if (d.mortgageTermsKnown) params.mortgage_terms_known = d.mortgageTermsKnown;
    if (d.mortgageYears.length) params.mortgage_years = d.mortgageYears;
  }
  if (d.listedAfter) params.listed_after = d.listedAfter;
  if (d.listedBefore) params.listed_before = d.listedBefore;
  if (d.hasBillOfSale) params.has_bill_of_sale = d.hasBillOfSale;
  if (d.deedGap) params.deed_gap = d.deedGap;
  if (d.minYield) params.min_yield = d.minYield;
  if (d.sellerType) params.seller_type = d.sellerType;
  if (d.locationIds.length) params.location_ids = d.locationIds;
  if (d.dealTiers.length) params.deal_tier = d.dealTiers;
  if (d.complexSlugs.length) params.complex_slug = d.complexSlugs;
  if (d.developers.length) params.developer = d.developers;
  if (d.streets.length) params.street = d.streets;
  if (d.cities.length) params.city = d.cities;
  if (d.draw) params.draw = d.draw;
  // Rent period only means something when rentals are in the search.
  if (d.rentPeriod && (d.leased === "true" || d.leased === "all")) params.rent_period = d.rentPeriod;
  if (d.metroWalk) params.metro_walk = d.metroWalk;
  // Radius is only committed when a centre has been picked. The backend
  // needs all three (center_lat/center_lng/radius_km) together.
  if (d.centerLat && d.centerLng) {
    params.center_lat = d.centerLat;
    params.center_lng = d.centerLng;
    params.radius_km = String(d.radiusKm);
  }
  const facts = factsOf(d);
  if (facts.length) params.fact = facts;
  return params;
}

/** The text facts the draft keeps: only those of the deal it searches
 *  (rent terms for rentals, the papers and the deed for sales), and none
 *  for a plot, a shop or a garage (their texts are not read). */
function factsOf(d: Draft): string[] {
  const kind = kindOf(d);
  if (kind !== "apartment" && kind !== "house") return [];
  const allowed = new Set<string>(topicsFor(d.leased).flatMap((g) => g.facts));
  return d.facts.filter((f) => allowed.has(f));
}

/** The kind the draft searches: its category, else the bar's merged
 *  listing type, else flats. */
function kindOf(d: Draft): string {
  return d.category || (d.listingKind.includes("land") ? "land" : d.listingKind.includes("house") ? "house" : "apartment");
}

/** A flat or a house: the kinds with a number of floors. */
function isHome(d: Draft): boolean {
  const kind = kindOf(d);
  return kind === "apartment" || kind === "house";
}

/** The topics past the main filters, each a titled row that opens in
 *  place, in the owner's order (2026-10-29, by use in 30 days, the papers
 *  first): the papers, the amenities, the source, the seller, the
 *  condition, the rent terms, the price check, the building, the photos,
 *  then when the listing came and whether you saw it. */
const SECTIONS = ["docs", "features", "source", "seller", "condition", "rent_terms", "review", "building", "photos", "listed", "viewed"] as const;
type SectionId = (typeof SECTIONS)[number];

/** How many choices each topic holds: a picked chip, a set range or an
 *  answered question counts once (a take-over's terms ride with it). */
function sectionPicks(d: Draft, offers: boolean): Record<SectionId, number> {
  const kind = kindOf(d);
  const facts = offers ? [] : factsOf(d);
  const inTopic = (id: FactTopic) => {
    const g = topicsFor(d.leased).find((x) => x.id === id);
    return g ? facts.filter((f) => (g.facts as readonly string[]).includes(f)).length : 0;
  };
  const n = (...xs: (boolean | number)[]) => xs.reduce<number>((sum, x) => sum + Number(x), 0);
  return {
    docs: n(!!d.hasMortgage, !!d.mortgageKind, !!d.deedGap),
    features: d.featureTags.length,
    source: d.sources.length,
    seller: n(facts.includes("owner") || !!d.sellerType),
    condition: inTopic("condition"),
    rent_terms: inTopic("rent_terms"),
    review: n(d.dealTiers.length, !!d.minYield),
    building: n(
      offers ? 0 : d.developers.length,
      isHome(d) && !!(d.totalFloorsMin || d.totalFloorsMax),
      // A plot's size is a main filter; a house's plot is not.
      kind !== "land" && !!(d.sotMin || d.sotMax),
      !!(d.ppsMin || d.ppsMax),
      !!(d.ppmMin || d.ppmMax),
      !!d.metroWalk,
    ),
    photos: d.photoConditions.length + d.photoHas.length,
    listed: n(!!(d.listedAfter || d.listedBefore)),
    viewed: n(!!d.viewed),
  };
}

export default function ListingsFilterPanel({
  open,
  onClose,
  searchParams,
  onSearch,
  onReset,
  footerExtras,
  headerExtras,
  showKind = false,
  forAlert = false,
  offers,
}: Props) {
  const t = useT();
  const viewedCount = useViewed().size;
  const { locale } = useLocale();
  // Amenity-tag vocabulary (static per deploy; module-cached fetch).
  const [tagVocab, setTagVocab] = useState<FeatureTagVocab | null>(null);
  // The site grid opens from its summary row (owner, 2026-10-17).
  const [sourcesOpen, setSourcesOpen] = useState(false);
  // Amenity rows opened past their first chips ("+N").
  const [openRows, setOpenRows] = useState<ReadonlySet<AmenityRow>>(new Set());
  // Typed text narrows the amenity chips (owner, 2026-10-12).
  const [tagQuery, setTagQuery] = useState("");
  useEffect(() => {
    getFeatureTags().then(setTagVocab).catch(() => {});
  }, []);
  // Verified developers (module-cached fetch), busiest first in the
  // short list; the full list opens behind "All".
  const [allDevelopers, setDevelopers] = useState<DeveloperRow[]>([]);
  const [showAllDevs, setShowAllDevs] = useState(false);
  const [devQuery, setDevQuery] = useState("");
  useEffect(() => {
    if (open && !offers) getDevelopers().then(setDevelopers).catch(() => {});
  }, [open, offers]);
  const developers = offers?.developers ?? allDevelopers;
  // Residences with offers (the offers page): the same suggesting list.
  const [showAllRes, setShowAllRes] = useState(false);
  const [resQuery, setResQuery] = useState("");

  // The draft is the local, uncommitted filter state. It re-seeds from
  // the URL whenever the committed params change (after a Search /
  // Reset / back-button navigation) so the panel always reflects what
  // the grid is actually showing once a search lands.
  const spKey = searchParams.toString();
  const [draft, setDraft] = useState<Draft>(() => draftFromParams(searchParams));
  useEffect(() => {
    setDraft(draftFromParams(searchParams));
    // Re-seed only when the committed URL changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spKey]);

  // One rule for every sheet on a phone (2026-10-29): closing it (a
  // swipe, Back, the x) applies what is staged, like the bottom button;
  // removing a chip above the list is the undo. The desktop modal stays a
  // dialog: closing it drops the draft. Set below, once the draft is known.
  const closeRef = useRef<() => void>(onClose);
  const closeSheet = useCallback(() => closeRef.current(), []);

  // Close on Escape; lock body scroll while open.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") closeSheet();
    }
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, closeSheet]);

  // Mobile bottom-sheet presentation: swipe down on the header closes.
  // On desktop the same DOM is a centered modal - the hook is inert
  // there (gated to < sm widths).
  const { offset, dragging, handleProps, panelRef } = useSheetDrag(closeSheet, open);
  // Hardware Back closes this overlay instead of leaving the page.
  useBackToClose(open, closeSheet);
  // Mounted while open or sliding out; `entered` drives the transition
  // (the same pattern as BottomSheet). The CSS keyframes this replaces
  // were gated by prefers-reduced-motion and so never ran on a phone
  // with Reduce Motion on: this way the panel still cross-fades there.
  const [render, setRender] = useState(false);
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    if (open) { setRender(true); return; }
    setEntered(false);
    const timer = setTimeout(() => setRender(false), PANEL_MS);
    return () => clearTimeout(timer);
  }, [open]);
  useEffect(() => {
    if (!render || !open) return;
    const raf = requestAnimationFrame(() => {
      // The closed position is computed before the open one, or the
      // transition has nothing to start from (a WebView mounting and
      // opening within one frame showed the panel in place).
      void document.body.offsetHeight;
      setEntered(true);
    });
    return () => cancelAnimationFrame(raf);
  }, [render, open]);

  // ── The topics past the main filters ───────────────────────────
  // One titled row per topic that opens in place (owner, 2026-10-29,
  // Airbnb's pattern, in place of one "Daha çox filtr" fold). A topic
  // with a choice made starts open, so a set filter is never out of
  // sight, and its row says how many are picked.
  const scrollRef = useRef<HTMLDivElement>(null);
  const [openSections, setOpenSections] = useState<ReadonlySet<SectionId>>(() => new Set());
  useEffect(() => {
    if (!open) return;
    const picks = sectionPicks(draftFromParams(searchParams), !!offers);
    setOpenSections(new Set(SECTIONS.filter((id) => picks[id] > 0)));
    // Read again on opening and when the committed URL changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, spKey]);
  const toggleSection = (id: SectionId) => {
    const next = !openSections.has(id);
    setOpenSections((cur) => {
      const out = new Set(cur);
      if (next) out.add(id);
      else out.delete(id);
      return out;
    });
    trackEvent("listings.filters.section", { id, open: next });
    if (!next) return;
    // Opened low on the screen, the row comes up so what it holds shows.
    requestAnimationFrame(() => {
      const el = scrollRef.current;
      const line = el?.querySelector<HTMLElement>(`[data-section="${id}"]`);
      if (!el || !line || line.offsetTop - el.scrollTop < el.clientHeight * 0.45) return;
      el.scrollTo({ top: Math.max(0, line.offsetTop - 12), behavior: prefersReducedMotion() ? "auto" : "smooth" });
    });
  };

  const set = useCallback(<K extends keyof Draft>(key: K, value: Draft[K]) => {
    setDraft((d) => ({ ...d, [key]: value }));
  }, []);

  // Each tap toggles that count (owner, 2026-10-29: no two-tap range).
  function toggleRoom(r: string) {
    setDraft((d) => ({ ...d, rooms: d.rooms.includes(r) ? d.rooms.filter((x) => x !== r) : [...d.rooms, r] }));
  }

  function toggleDealTier(tier: string) {
    setDraft((d) => ({
      ...d,
      dealTiers: d.dealTiers.includes(tier)
        ? d.dealTiers.filter((x) => x !== tier)
        : [...d.dealTiers, tier],
    }));
  }

  // Commit the draft and close the modal.
  function runSearch() {
    haptic(10);
    onSearch(draftToParams(draft));
    onClose();
  }

  // ── Live match count for the current draft ──
  // The Apply button carries the payoff ("Axtar · 1 243") instead of
  // asking for a blind commit - the count IS the reason to click.
  // Debounced; page_size=1 so only the cheap total comes back.
  const [matchCount, setMatchCount] = useState<number | null>(null);
  const draftKey = JSON.stringify(draft);
  useEffect(() => {
    if (!open) return;
    const ctrl = new AbortController();
    if (offers) {
      const tid = setTimeout(() => {
        offers.count(draftToParams(draft)).then((n) => { if (!ctrl.signal.aborted) setMatchCount(n); }).catch(() => {});
      }, 450);
      return () => { clearTimeout(tid); ctrl.abort(); };
    }
    const tid = setTimeout(() => {
      // The grid's own rules (lib/listing-search-params): the sale
      // default, "5+" rooms as every count from five, the drawn areas.
      const sp = new URLSearchParams();
      for (const [k, v] of Object.entries(draftToParams(draft))) {
        if (Array.isArray(v)) v.forEach((i) => { if (i) sp.append(k, i); });
        else if (v) sp.set(k, String(v));
      }
      const qp = new URLSearchParams();
      for (const [k, v] of Object.entries(apiParamsFromSearch(sp as unknown as ReadonlyURLSearchParams))) {
        if (Array.isArray(v)) v.forEach((i) => { if (i) qp.append(k, i); });
        else if (v) qp.set(k, String(v));
      }
      qp.set("page_size", "1");
      fetch(`/api/listings?${qp.toString()}`, { signal: ctrl.signal })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (d && !ctrl.signal.aborted && typeof d.total === "number") {
            setMatchCount(d.total);
          }
        })
        .catch(() => {});
    }, 450);
    return () => { clearTimeout(tid); ctrl.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftKey, open]);

  // Clear the draft + URL but keep the modal open so the user sees the
  // cleared state.
  function reset() {
    setDraft(EMPTY_DRAFT);
    onReset();
  }

  // Phones get vertical check lists for the long pickers and three chips
  // per amenity row.
  const isMobile = useIsMobileWidth();
  const committedKey = JSON.stringify(draftToParams(draftFromParams(searchParams)));
  closeRef.current = () => {
    const params = draftToParams(draft);
    if (isMobile && JSON.stringify(params) !== committedKey) onSearch(params);
    onClose();
  };
  // The reset button's count is the one count every surface uses (the
  // bar's badge, the reset buttons: lib/active-filter-count), on the draft.
  const totalActive = countActiveFilters(draftToParams(draft));
  // The draft as a query: the alert beside the count is for exactly
  // what the count counts.
  const draftQuery = useMemo(() => {
    const sp = new URLSearchParams();
    for (const [k, v] of Object.entries(draftToParams(draft))) {
      if (Array.isArray(v)) v.forEach((i) => { if (i) sp.append(k, i); });
      else if (v) sp.set(k, String(v));
    }
    return sp as unknown as ReadonlyURLSearchParams;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftKey]);
  const picks = sectionPicks(draft, !!offers);

  // ── Controls ────────────────────────────────────────────────────
  // Colour only on a choice (owner, 2026-10-27: "nothing is coloured
  // until you pick it"). An option is white until it is picked, then gets
  // the 2 px dark outline like the filter bar's pills (the quiet UI,
  // 2026-10-29: no indigo fill); a value that always has one (the deal,
  // the kind, the rent type) is a light track with an outlined white
  // segment. No option stands for "any": an unpicked filter is that.
  const optBase = (on: boolean) =>
    `inline-flex h-9 min-w-[2.75rem] items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border text-sm font-medium tabular-nums transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
      on ? SELECTED : UNSELECTED
    }`;
  const opt = (on: boolean) => `${optBase(on)} px-3`;
  // A long choice wraps inside its segment on a phone instead of
  // pushing the track past the screen ("Elandakından az", ru labels).
  const segItem = (on: boolean) =>
    `flex-1 whitespace-normal leading-tight rounded-lg px-2.5 py-1.5 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 sm:whitespace-nowrap ${
      on ? SEGMENT_ON : SEGMENT_OFF
    }`;
  const segmented = (value: string, options: { value: string; label: string }[], onPick: (v: string) => void, label: string) => (
    <div role="radiogroup" aria-label={label} className="flex w-full rounded-xl bg-gray-100 p-1 sm:w-fit">
      {options.map((o) => (
        <button
          key={o.value || "default"}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          onClick={() => onPick(o.value)}
          // Four choices share a phone's width by their words, so none wraps.
          className={`${segItem(value === o.value)} ${options.length > 3 ? "max-sm:flex-auto max-sm:px-2" : ""} sm:px-4`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
  /** An optional yes / no (owner, 2026-10-29): the light track with a
   *  white segment, "Fərq etməz" first and picked by default, so the
   *  filter's off state is a choice you can see. */
  const yesNo = (value: string, labels: [string, string], onPick: (v: string) => void, label: string) => segmented(
    value === "yes" || value === "no" ? value : "",
    [{ value: "", label: t("listings.filter.any") }, { value: "yes", label: labels[0] }, { value: "no", label: labels[1] }],
    onPick,
    label,
  );
  const field = "w-full min-w-0 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm tabular-nums text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500";
  // A box with a value wears the set filters' faint brand tint (option D).
  const fieldSet = field.replace("border-gray-200 bg-white", "border-indigo-200 bg-indigo-50").replace("text-gray-800", "text-indigo-700");
  /** One box of a range, its unit inside on the right. */
  const unitBox = (input: React.ReactNode, unit: string | null) => (
    <div className="relative min-w-0 flex-1">
      {input}
      {unit && (
        <span aria-hidden className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-gray-400">{unit}</span>
      )}
    </div>
  );
  const pair = (lo: React.ReactNode, hi: React.ReactNode) => (
    <div className="flex items-center gap-2">
      {lo}
      <span aria-hidden className="text-gray-300">-</span>
      {hi}
    </div>
  );
  type NumKey = "areaMin" | "areaMax" | "sotMin" | "sotMax" | "ppsMin" | "ppsMax" | "ppmMin" | "ppmMax" | "floorMin" | "floorMax"
    | "totalFloorsMin" | "totalFloorsMax";
  const numBox = (key: NumKey, unit: string | null, label: string, step?: string) => {
    const lo = key.endsWith("Min");
    return unitBox(
      <input
        type="number"
        min={0}
        step={step}
        value={draft[key]}
        onChange={(e) => set(key, e.target.value)}
        placeholder={t(lo ? "listings.filter.area.min" : "listings.filter.area.max")}
        aria-label={`${label}, ${t(lo ? "listings.filter.price.min" : "listings.filter.price.max")}`}
        className={`${draft[key] ? fieldSet : field} ${unit ? (unit.length > 2 ? "pr-11" : "pr-9") : ""}`}
      />,
      unit,
    );
  };
  /** One filter: its label on the left on desktop, above it on phones,
   *  with the icon that says what it is. */
  const ROW_ICON: Record<string, LucideIcon> = {
    kind: ICON.kind, deal: ICON.deal, rent_period: ICON.rentPeriod, place: ICON.place, rooms: ICON.rooms,
    price: ICON.price, area: ICON.area, repair: ICON.repair, floor: ICON.floor, deed: ICON.deed,
    mortgage: ICON.mortgage, deed_gap: ICON.deedGap, takeover: ICON.takeover, seller: ICON.seller,
    deal_tier: ICON.review, yield: ICON.yield, listed: ICON.listing, source: ICON.source, viewed: ICON.viewed,
    build: ICON.building, developer: ICON.developer, residence: ICON.building, metro: ICON.metro,
    floor_range: ICON.floor, total_floors: ICON.building, sot: ICON.plot, pps: ICON.price, ppm: ICON.price, photo_tier: ICON.photos,
    photo_has: ICON.photoHas,
  };
  const row = (key: string, label: string, control: React.ReactNode, Icon: LucideIcon | undefined = ROW_ICON[key]) => (
    <div key={key} className="sm:grid sm:grid-cols-[9rem_minmax(0,1fr)] sm:items-start sm:gap-4">
      <p className="mb-1.5 flex items-center gap-1.5 text-sm text-gray-600 sm:mb-0 sm:pt-2">
        {Icon && <Icon className="h-4 w-4 shrink-0 text-gray-400" aria-hidden />}
        {label}
      </p>
      <div className="min-w-0">{control}</div>
    </div>
  );
  /** Phone-only vertical check list (the sort sheet's row style). */
  const optionList = (
    options: { value: string; label: string; lead?: React.ReactNode }[],
    selected: string[],
    onToggle: (v: string) => void,
  ) => (
    <div role="listbox" aria-multiselectable="true" className="flex flex-col">
      {options.map(({ value, label, lead }) => {
        const on = selected.includes(value);
        return (
          <button
            key={value || "any"}
            type="button"
            role="option"
            aria-selected={on}
            onClick={() => onToggle(value)}
            className={`flex items-center justify-between gap-3 px-3 py-2.5 text-sm rounded-lg transition-colors ${
              on ? ROW_ON : "text-gray-700 hover:bg-gray-50"
            }`}
          >
            <span className="flex items-center gap-2">{lead}{label}</span>
            {on && <Check className="w-4 h-4 shrink-0" />}
          </button>
        );
      })}
    </div>
  );
  // "All (141)", "+6", "Less": plain words, so nothing looks like a
  // filter that is not one.
  const moreWord = "inline-flex h-9 items-center px-1.5 text-sm font-semibold tabular-nums text-gray-500 transition-colors hover:text-gray-900";

  function renderContent() {
    const kindNow = kindOf(draft);
    // Commercial premises and garages (2026-10-21): their own filters only.
    const separate = isSeparate(kindNow);
    const build = draft.listingKind.filter((k) => k === "new" || k === "old");
    const setKind = (k: Exclude<ListingKind, "">) => { trackEvent("listings.kind", { kind: k, where: showKind ? "map" : "list" }); setDraft((d) => ({
      ...d,
      category: k,
      propertyKinds: [],
      // Rooms, floors and the build belong to flats and houses; a plot has
      // a size in sot instead (mirrors the bar's kindChangePatch).
      listingKind: k === "apartment" ? d.listingKind.filter((x) => x === "new" || x === "old") : [],
      ...(k === "land" ? { rooms: [], floorMin: "", floorMax: "", totalFloorsMin: "", totalFloorsMax: "", excludeFirstFloor: false, excludeLastFloor: false } : { sotMin: "", sotMax: "", ppsMin: "", ppsMax: "" }),
      // A shop or a garage has no rooms, residence, valuation, mortgage,
      // photo tier or amenities; a garage no floor either.
      ...(isSeparate(k) ? {
        rooms: [], buildingType: "", complexSlugs: [], developers: [], dealTiers: [], minYield: "", hasMortgage: "",
        mortgageKind: "", maxInitial: "", mortgageRateMin: "", mortgageRateMax: "", mortgageTermsKnown: "", mortgageYears: [],
        photoConditions: [], featureTags: [], deedGap: "", rentPeriod: "", excludeFirstFloor: false, excludeLastFloor: false,
        totalFloorsMin: "", totalFloorsMax: "",
        ...(k === "garage" ? { floorMin: "", floorMax: "", hasRepair: "" } : {}),
      } : { ppmMin: "", ppmMax: "" }),
    })); };
    const fold = (v: string) => v.toLocaleLowerCase("az").normalize("NFD").replace(/[̀-ͯ]/g, "")
      .replace(/ə/g, "e").replace(/ı/g, "i").replace(/ş/g, "s").replace(/ç/g, "c").replace(/ğ/g, "g")
      .replace(/ö/g, "o").replace(/ü/g, "u");
    const searchBox = (value: string, onChange: (v: string) => void, placeholder: string) => (
      <div className="relative mb-2">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden />
        <input type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} aria-label={placeholder} className={`${field} pl-9`} />
      </div>
    );
    // The developers (owner, 2026-10-29: "far too tall"): the six with the
    // most listings as logo tiles in two rows, then "Hamısı (141)" opens
    // the whole list with a search box. A picked developer outside the
    // six joins the tiles, so a choice is never out of sight.
    const developerFilter = developers.length > 0 && (() => {
      const toggle = (slug: string) =>
        setDraft((d) => ({
          ...d,
          developers: d.developers.includes(slug) ? d.developers.filter((x) => x !== slug) : [...d.developers, slug],
        }));
      const top = [...developers].sort((a, b) => (b.sale_n + b.rent_n) - (a.sale_n + a.rent_n)).slice(0, 6);
      const tiles = [...top, ...developers.filter((d) => draft.developers.includes(d.slug) && !top.some((b) => b.slug === d.slug))];
      const q = fold(devQuery.trim());
      const listed = q ? developers.filter((d) => [d.name, ...d.aliases].some((n) => fold(n).includes(q))) : developers;
      return (
        <div>
          <div className="grid grid-cols-3 gap-2">
            {tiles.map((d) => {
              const on = draft.developers.includes(d.slug);
              return (
                <button
                  key={d.slug}
                  type="button"
                  aria-pressed={on}
                  onClick={() => toggle(d.slug)}
                  title={d.name}
                  className={`flex min-w-0 flex-col items-center justify-center gap-1 rounded-lg border px-1.5 py-2 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
                    on ? SELECTED : UNSELECTED
                  }`}
                >
                  <DeveloperLogo name={d.name} logo={d.logo} size={28} />
                  <span className="w-full truncate text-center">{d.name}</span>
                </button>
              );
            })}
          </div>
          {showAllDevs ? (
            <div className="mt-2 rounded-xl border border-gray-200 p-2">
              {searchBox(devQuery, setDevQuery, t("listings.filter.developer.search"))}
              <div className="max-h-64 overflow-y-auto">
                {optionList(listed.map((d) => ({ value: d.slug, label: d.name, lead: <DeveloperLogo name={d.name} logo={d.logo} size={24} /> })), draft.developers, toggle)}
                {listed.length === 0 && <p className="px-1 py-2 text-sm text-gray-500">{t("listings.filter.developer.empty")}</p>}
              </div>
              <button type="button" onClick={() => { setShowAllDevs(false); setDevQuery(""); }} className={moreWord}>
                {t("listings.filter.amenities_less")}
              </button>
            </div>
          ) : developers.length > top.length && (
            <button type="button" onClick={() => setShowAllDevs(true)} className={moreWord}>
              {t("listings.filter.developer.all").replace("{n}", String(developers.length))}
            </button>
          )}
        </div>
      );
    })();

    // The offers page's residences, the developer picker's twin: typing
    // searches them all, with nothing typed the busiest eight show and
    // "all" opens the whole list.
    const residenceFilter = !!offers && offers.residences.length > 0 && (() => {
      const toggle = (id: string) =>
        setDraft((d) => ({
          ...d,
          complexSlugs: d.complexSlugs.includes(id) ? d.complexSlugs.filter((x) => x !== id) : [...d.complexSlugs, id],
        }));
      const all = offers.residences;
      const busiest = [...all].sort((x, y) => y.n - x.n).slice(0, 8);
      const selectedExtra = all.filter((r) => draft.complexSlugs.includes(r.cluster_id) && !busiest.some((b) => b.cluster_id === r.cluster_id));
      const q = fold(resQuery.trim());
      const visible = q ? all.filter((r) => fold(r.name).includes(q)) : showAllRes ? all : [...busiest, ...selectedExtra];
      return (
        <div>
          {searchBox(resQuery, setResQuery, t("listings.location.search"))}
          {isMobile ? (
            optionList(visible.map((r) => ({ value: r.cluster_id, label: r.name })), draft.complexSlugs, toggle)
          ) : (
            <div className={`flex flex-wrap gap-2 ${showAllRes || q ? "max-h-64 overflow-y-auto" : ""}`}>
              {visible.map((r) => {
                const on = draft.complexSlugs.includes(r.cluster_id);
                return (
                  <button key={r.cluster_id} type="button" aria-pressed={on} onClick={() => toggle(r.cluster_id)} className={opt(on)}>
                    {r.name}
                  </button>
                );
              })}
            </div>
          )}
          {visible.length === 0 && (
            <p className="px-1 py-2 text-sm text-gray-500">{t("listings.location.empty")}</p>
          )}
          {!q && all.length > busiest.length && (
            <button type="button" onClick={() => setShowAllRes((v) => !v)} className={moreWord}>
              {showAllRes ? t("listings.filter.amenities_less") : t("listings.filter.developer.all").replace("{n}", String(all.length))}
            </button>
          )}
        </div>
      );
    })();

    // A plot has no repair, no mortgage and no floors: land keeps only the
    // deed (owner, 2026-10-07).
    const landOnly = draft.category === "land" || (draft.listingKind.length > 0 && draft.listingKind.every((k) => k === "land"));
    // A garage keeps the deed only, like a plot; a shop keeps its repair.
    const deedOnly = landOnly || kindNow === "garage";
    // A shop's floor: the ground floor is the one a business asks for,
    // so it is one tap (floor 1 to 1); the range waits under "more".
    const groundOnly = draft.floorMin === "1" && draft.floorMax === "1";
    const yesNoLabels: [string, string] = [t("listings.filter.tri.yes"), t("listings.filter.tri.no")];

    // ── Lead: the kind where the page has no pill for it (the map, owner
    // 2026-10-12), and the offers page's developers and residences.
    const lead = (
      <>
        {offers && developerFilter && row("developer", t("listings.filter.developer"), developerFilter)}
        {residenceFilter && row("residence", t("listings.location.tab.complex"), residenceFilter)}
      </>
    );

    // The facts read from the text (lib/text-facts) are for homes; the
    // offers page counts offers, which carry no reading.
    const factsShown = !offers && (kindNow === "apartment" || kindNow === "house");
    const topics = factsShown ? topicsFor(draft.leased) : [];
    const topicOn = (id: FactTopic) => topics.some((g) => g.id === id);

    // ── What the listing's own text says (lib/text-facts) ─────────────
    // Inside the topics a buyer looks in (owner's pick, 2026-10-29: the
    // documents, the seller, the condition, the rent terms), each fact
    // with its one-line explainer and one grey "read from the text" note
    // per topic. Homes only (a plot's, a shop's or a garage's text is not
    // read); the offers page counts offers, which carry no reading.
    const toggleFact = (f: FactFilter) => setDraft((d) => {
      const on = d.facts.includes(f);
      return { ...d, facts: on ? d.facts.filter((x) => x !== f) : [...d.facts, f] };
    });
    const sourceNote = (
      <p className="flex items-start gap-1.5 rounded-lg bg-gray-100 px-3 py-2 text-xs leading-snug text-gray-600">
        <Info className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
        {t("listings.fact.source")}
      </p>
    );
    const hint = (text: string) => <p className="mt-1 text-xs leading-snug text-gray-500">{text}</p>;
    /** A row with no label of its own: the control sits under the one
     *  above it (desktop: in the controls column). */
    const subRow = (key: string, control: React.ReactNode) => (
      <div key={key} className="sm:grid sm:grid-cols-[9rem_minmax(0,1fr)] sm:gap-4">
        <div className="min-w-0 sm:col-start-2">{control}</div>
      </div>
    );
    const factItem = (f: FactFilter) => {
      const on = draft.facts.includes(f);
      const Icon = FACT_ICON[f];
      if (FACT_SWITCHES.has(f)) {
        return (
          <div key={f}>
            <div className="flex items-center justify-between gap-3">
              <span className="flex items-center gap-1.5 text-sm text-gray-800">
                <Icon className="h-4 w-4 shrink-0 text-gray-400" aria-hidden />
                {t(FACT_LABEL[f])}
              </span>
              <AlertSwitch on={on} label={t(FACT_LABEL[f])} onToggle={() => toggleFact(f)} />
            </div>
            {hint(t(FACT_HINT[f]))}
          </div>
        );
      }
      return (
        <div key={f}>
          <button type="button" aria-pressed={on} onClick={() => toggleFact(f)} className={opt(on)}>
            <Icon className={`h-3.5 w-3.5 shrink-0 ${on ? "text-gray-900" : "text-gray-400"}`} aria-hidden />
            {t(FACT_LABEL[f])}
          </button>
          {hint(t(FACT_HINT[f]))}
        </div>
      );
    };
    /** One fact under the row it belongs to (the deed, the mortgage). */
    const factRow = (f: FactFilter) => (topics.some((g) => g.facts.includes(f)) ? subRow(`fact_${f}`, factItem(f)) : null);
    const factBlock = (id: FactTopic) => {
      const g = topics.find((x) => x.id === id);
      return g ? row(`facts_${id}`, t("listings.fact.from_text"), (
        <div className="space-y-3">
          {g.facts.filter((f) => f !== "owner").map(factItem)}
        </div>
      ), ICON.fromText) : null;
    };
    // The seller (owner, 2026-10-29): "Fərq etməz" first, then from the
    // owner, read from the text with the site's owner label checked
    // against the seller's book, taking the old owner pick's place; agent
    // and company stay the site's labels. A plot's or a shop's text is
    // not read: there "owner" is the site's label, as before.
    const sellerValue = draft.facts.includes("owner") ? "owner" : draft.sellerType;
    const seller = !offers && (
      <div className="space-y-3">
        {segmented(
          sellerValue === "owner" || sellerValue === "agent" || sellerValue === "company" ? sellerValue : "",
          [
            { value: "", label: t("listings.filter.any") },
            { value: "owner", label: factsShown ? t("listings.fact.owner.short") : t("listings.filter.seller_type.owner") },
            { value: "agent", label: t("listings.filter.seller_type.agent") },
            { value: "company", label: t("listings.filter.seller_type.company") },
          ],
          (v) => setDraft((d) => ({
            ...d,
            facts: v === "owner" && factsShown ? [...d.facts.filter((x) => x !== "owner"), "owner"] : d.facts.filter((x) => x !== "owner"),
            sellerType: v === "owner" && factsShown ? "" : v,
          })),
          t("listings.filter.seller_type"),
        )}
        {factsShown && sellerValue === "owner" && <p className="text-xs leading-snug text-gray-500">{t("listings.fact.owner.hint")}</p>}
      </div>
    );

    // ── The main filters, always open (owner, 2026-10-29), in this
    // order: the deal, the kind, the place, the rooms, the price, the
    // area, the deed (with "the deed comes later" under it), the repair,
    // the floor.
    // The kind in two levels (owner, 2026-10-29), as the desktop bar's
    // kind pill offers it (the same params, so the two stay in sync).
    // Row 1: the five types, each with its icon; none picked browses flats
    // and houses, the picked one taps off. Row 2, under a clear gap and
    // only for a type with subtypes: the light track with "Fərq etməz"
    // first, a flat's build, an office or an obyekt, a garage or a parking
    // space, on the keys it always had (listing_kind, property_kind).
    const kindPicked: ListingKind = (LISTING_KINDS as readonly string[]).includes(draft.category)
      ? (draft.category as ListingKind)
      : draft.listingKind.includes("land") ? "land" : draft.listingKind.includes("house") ? "house"
        // A build is a flat's: a link with only the build shows the flat.
        : build.length ? "apartment" : "";
    const clearKind = () => setDraft((d) => ({
      ...d, category: "", listingKind: [], propertyKinds: [], sotMin: "", sotMax: "", ppsMin: "", ppsMax: "", ppmMin: "", ppmMax: "",
    }));
    const subtypes: { value: string; label: string }[] = kindPicked === "apartment"
      ? [{ value: "new", label: t("listings.filter.building_type.new") }, { value: "old", label: t("listings.filter.building_type.old") }]
      : isSeparate(kindPicked) ? kindsOf(kindPicked).map((k) => ({ value: k, label: t(kindChipKey(k)) })) : [];
    // Both picked (an older link) is the same as none: "Fərq etməz".
    const subtypeValue = kindPicked === "apartment"
      ? (build.length === 1 ? build[0] : "")
      : (draft.propertyKinds.length === 1 ? draft.propertyKinds[0] : "");
    const pickSubtype = (v: string) => setDraft((d) => (kindPicked === "apartment"
      ? { ...d, category: d.category || "apartment", listingKind: v ? [v] : [] }
      : { ...d, propertyKinds: v ? [v] : [] }));
    const kindRow = !offers && row("kind", t("listings.filter.category"), (
      <div>
        <div role="group" aria-label={t("listings.filter.category")} className="flex flex-wrap gap-2">
          {LISTING_KINDS.map((value) => {
            const on = kindPicked === value;
            const Icon = KIND_ICON[value];
            return (
              <button
                key={value}
                type="button"
                aria-pressed={on}
                onClick={() => (on ? clearKind() : setKind(value))}
                className={opt(on)}
              >
                <Icon className={`h-3.5 w-3.5 shrink-0 ${on ? "text-gray-900" : "text-gray-400"}`} aria-hidden />
                {t(`listings.filter.category.${value}` as const)}
              </button>
            );
          })}
        </div>
        {subtypes.length > 0 && (
          <div className="mt-3">
            {segmented(
              subtypeValue,
              [{ value: "", label: t("listings.filter.any") }, ...subtypes],
              pickSubtype,
              kindPicked === "apartment" ? t("listings.filter.building") : t("listings.filter.property_kind"),
            )}
          </div>
        )}
      </div>
    ));
    const roomsShown = kindNow !== "land" && (!separate || officesOnly(kindNow, draft.propertyKinds));
    const priceBox = (key: "priceMin" | "priceMax") => {
      const lo = key === "priceMin";
      return unitBox(
        <input
          type="text"
          inputMode="numeric"
          value={draft[key].replace(/\B(?=(\d{3})+(?!\d))/g, " ")}
          onChange={(e) => set(key, e.target.value.replace(/\D/g, "").replace(/^0+(?=\d)/, "").slice(0, 8))}
          placeholder={t(lo ? "listings.filter.price.min" : "listings.filter.price.max")}
          aria-label={`${t("listings.filter.ess.price")}, ${t(lo ? "listings.filter.price.min" : "listings.filter.price.max")}`}
          className={`${draft[key] ? fieldSet : field} pr-12`}
        />,
        "AZN",
      );
    };
    const essentials = (
      <>
        {!offers && row("deal", t("listings.filter.transaction"), segmented(
          draft.leased === "true" || draft.leased === "all" ? draft.leased : "",
          [
            { value: "", label: t("listings.filter.transaction.sale") },
            { value: "true", label: t("listings.filter.transaction.rent") },
            { value: "all", label: t("listings.filter.transaction.both") },
          ],
          (v) => set("leased", v),
          t("listings.filter.transaction"),
        ))}
        {draft.leased === "true" && !offers && !separate && row("rent_period", t("listings.filter.rent_period"), segmented(
          draft.rentPeriod,
          [
            { value: "", label: t("listings.filter.rent_period.monthly") },
            { value: "daily", label: t("listings.filter.rent_period.daily") },
            { value: "room", label: t("listings.filter.rent_period.room") },
            { value: "all", label: t("listings.filter.rent_period.all") },
          ],
          (v) => set("rentPeriod", v),
          t("listings.filter.rent_period"),
        ))}
        {kindRow}
        {row("place", t("listings.filter.ess.place"), (
          <ListingsLocationPicker
            countBase={offers ? undefined : draftQuery}
            selectedIds={draft.locationIds}
            onChange={(ids) => set("locationIds", ids)}
            // A residence is a place of homes: none for a shop or a garage.
            selectedComplexSlugs={separate ? undefined : draft.complexSlugs}
            onComplexChange={separate ? undefined : (slugs) => set("complexSlugs", slugs)}
            selectedStreets={draft.streets}
            onStreetsChange={(ids) => set("streets", ids)}
          />
        ))}
        {/* A plot has no rooms; a shop or a garage has none either (an
            office has). */}
        {roomsShown && row("rooms", t("listings.filter.ess.rooms"), (
          <div role="group" aria-label={t("listings.filter.ess.rooms")} className="flex gap-2">
            {ROOMS.map((r) => (
              <button key={r} type="button" aria-pressed={draft.rooms.includes(r)} onClick={() => toggleRoom(r)} className={`${opt(draft.rooms.includes(r))} flex-1 sm:flex-none`}>
                {r === "5" ? t("listings.filter.rooms.5plus") : r}
              </button>
            ))}
          </div>
        ))}
        {row("price", t("listings.filter.ess.price"), pair(priceBox("priceMin"), priceBox("priceMax")))}
        {/* A plot's size is in sot (owner, 2026-10-12: the filters follow
            the kind). */}
        {kindNow === "land"
          ? row("area", t("listings.filter.ess.area"), pair(numBox("sotMin", t("listings.filter.unit.sot"), t("listings.filter.ess.area"), "0.1"), numBox("sotMax", t("listings.filter.unit.sot"), t("listings.filter.ess.area"), "0.1")))
          : row("area", t("listings.filter.ess.area"), pair(numBox("areaMin", "m²", t("listings.filter.ess.area")), numBox("areaMax", "m²", t("listings.filter.ess.area"))))}
        {/* A house's plot (owner, 2026-10-05: "how much land they have"),
            right under its floor area: further down, among the building's
            rows, a dacha buyer could not find it (ticket 68, 2026-11-01). */}
        {kindNow === "house" && row("sot", t("listings.filter.sot"), pair(
          numBox("sotMin", t("listings.filter.unit.sot"), t("listings.filter.sot"), "0.1"),
          numBox("sotMax", t("listings.filter.unit.sot"), t("listings.filter.sot"), "0.1"),
        ))}
        {/* The deed matters more than the rest (owner, 2026-10-29): a main
            filter, with the text's own word on it right under. */}
        {row("deed", t("listings.filter.deed"), yesNo(draft.hasBillOfSale, yesNoLabels, (v) => set("hasBillOfSale", v), t("listings.filter.deed")))}
        {factRow("no_deed_later")}
        {/* One question (2026-10-29): what the text says about the repair
            decides, and the site's tick when it says nothing. */}
        {!deedOnly && row("repair", t("listings.filter.repair"), (
          <div>
            {yesNo(
              draft.hasRepair,
              yesNoLabels,
              (value) =>
                setDraft((d) => ({
                  ...d,
                  hasRepair: value,
                  // A repair answer drops the photo tiers it excludes.
                  photoConditions:
                    value === "yes" ? d.photoConditions.filter((x) => x !== "shell")
                      : value === "no" ? d.photoConditions.filter((x) => x === "shell")
                        : d.photoConditions,
                })),
              t("listings.filter.repair"),
            )}
            {factsShown && draft.hasRepair && <p className="mt-1.5 text-xs leading-snug text-gray-500">{t("listings.fact.merged.hint")}</p>}
          </div>
        ))}
        {/* The floor range sits with its two shortcuts: in the folded
            building section nobody found it (feedback ticket 66). */}
        {kindNow === "apartment" && row("floor", t("listings.filter.floor"), (
          <div className="space-y-2">
            {pair(
              numBox("floorMin", null, t("listings.filter.floor")),
              numBox("floorMax", null, t("listings.filter.floor")),
            )}
            <div role="group" aria-label={t("listings.filter.floor")} className="flex gap-2">
              {([
                { key: "excludeFirstFloor", label: t("listings.filter.not_first"), on: draft.excludeFirstFloor },
                { key: "excludeLastFloor", label: t("listings.filter.not_last"), on: draft.excludeLastFloor },
              ] as const).map(({ key, label, on }) => (
                <button key={key} type="button" aria-pressed={on} onClick={() => set(key, !on)} className={`${opt(on)} flex-1 sm:flex-none`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
        ))}
        {kindNow === "commercial" && row("floor", t("listings.filter.floor"), (
          <div className="space-y-2">
            {pair(
              numBox("floorMin", null, t("listings.filter.floor")),
              numBox("floorMax", null, t("listings.filter.floor")),
            )}
            <button
              type="button"
              aria-pressed={groundOnly}
              onClick={() => setDraft((d) => ({ ...d, floorMin: groundOnly ? "" : "1", floorMax: groundOnly ? "" : "1" }))}
              className={opt(groundOnly)}
            >
              {t("listings.filter.ground_floor")}
            </button>
          </div>
        ))}
      </>
    );

    // ── The topics past the main filters ─────────────────────────────
    // 1. The home or the plot: what the photos say of the repair, the
    //    floor range, the plot, the price per unit, the developer, metro.
    const photoTiers = (
      <div className="flex flex-wrap gap-2">
        {PHOTO_TIERS.map((value) => {
          const on = draft.photoConditions.includes(value);
          return (
            <button
              key={value}
              type="button"
              aria-pressed={on}
              onClick={() =>
                setDraft((d) => {
                  const was = d.photoConditions.includes(value);
                  const next = was ? d.photoConditions.filter((x) => x !== value) : [...d.photoConditions, value];
                  let hasRepair = d.hasRepair;
                  if (!was) {
                    if (value === "shell" && hasRepair === "yes") hasRepair = "";
                    if (value !== "shell" && hasRepair === "no") hasRepair = "";
                  }
                  return { ...d, photoConditions: next, hasRepair };
                })
              }
              className={opt(on)}
            >
              {t(`photointel.tier.${value}` as TranslationKey)}
            </button>
          );
        })}
      </div>
    );
    const showPhotoTier = !deedOnly && !separate;
    const showTotalFloors = kindNow === "apartment" || kindNow === "house";
    const showDev = !offers && kindNow === "apartment" && !!developerFilter;
    const showMetro = kindNow !== "land";
    // The build of a flat (new or old) is the kind row's, as on the bar.
    const building = (
      <>
        {showDev && row("developer", t("listings.filter.developer"), developerFilter)}
        {/* The number of floors (ticket 63): the building's for a flat, the
            house's own for a house. bina.az states it for no house, so a
            house search with it keeps only the houses that do say: the
            hint says so. */}
        {showTotalFloors && row("total_floors", t("listings.filter.total_floors"), (
          <div>
            {pair(
              numBox("totalFloorsMin", null, t("listings.filter.total_floors")),
              numBox("totalFloorsMax", null, t("listings.filter.total_floors")),
            )}
            {kindNow === "house" && hint(t("listings.filter.total_floors.house_hint"))}
          </div>
        ))}
        {kindNow === "land" && row("pps", t("listings.filter.pps"), pair(
          numBox("ppsMin", null, t("listings.filter.pps")),
          numBox("ppsMax", null, t("listings.filter.pps")),
        ))}
        {/* A shop or a garage is compared by its price per m², the way a
            plot is by its price per sot. */}
        {separate && row("ppm", t("listings.filter.ppm"), pair(
          numBox("ppmMin", null, t("listings.filter.ppm")),
          numBox("ppmMax", null, t("listings.filter.ppm")),
        ))}
        {/* Near a metro matters to a shop as much as to a home. */}
        {showMetro && row("metro", t("listings.filter.metro_walk"), (
          <div className="flex flex-wrap gap-2">
            {["5", "10", "15"].map((m) => (
              <button key={m} type="button" aria-pressed={draft.metroWalk === m} onClick={() => set("metroWalk", draft.metroWalk === m ? "" : m)} className={opt(draft.metroWalk === m)}>
                {t("listings.filter.metro_walk.min").replace("{n}", m)}
              </button>
            ))}
          </div>
        ))}
      </>
    );
    const buildingVisible = kindNow === "house" || kindNow === "land" || separate || showDev || showMetro;

    // 2. İmkanlar: the amenity tags in rows by what they describe, each
    //    with its most asked-for chips and its own "+N"; the search finds
    //    any tag; what the photos show is a row of its own.
    const amenityRows = tagVocab && (() => {
      // Daily and room / bed are the rent type above, not chips.
      // "Depozitsiz" and "Sahibindən" are their topics' facts now (the
      // deposit fact counts this tag too), so they are not chips twice.
      const factTags = factsShown ? new Set(["deposit_free", "from_owner"]) : new Set<string>();
      const eligible = tagVocab.tags.filter((d) =>
        (draft.leased === "true" || d.category !== "rent_terms") && d.id !== "daily_rent" && d.id !== "room_share"
        && (!factTags.has(d.id) || draft.featureTags.includes(d.id))
        // A tag about one kind (an A-frame is a house) shows for that kind.
        && (!d.kinds || d.kinds.includes(kindNow) || draft.featureTags.includes(d.id)));
      // One order at every width: the camera chips, then the most used
      // (30 days), then the vocabulary's popular list, the rest in the
      // vocabulary's own order. A row shows the first chips of it: 3 on
      // phones, 4 on desktop.
      const used = new Map(USED_FIRST.map((id, i) => [id, 10 + i]));
      const rank = new Map(tagVocab.popular.map((id, i) => [id, 100 + i]));
      // A chip made for this kind comes right after the camera chips.
      const order = new Map(eligible.map((d, i) => [d.id, PHOTO_CONFIRMED.has(d.id) ? 0 : d.kinds ? 1 : (used.get(d.id) ?? rank.get(d.id) ?? 1000 + i)]));
      const labelOf = (def: (typeof eligible)[number]) => def.labels[locale as keyof typeof def.labels] ?? def.labels.az;
      const toggleTag = (id: string) =>
        setDraft((d) => ({ ...d, featureTags: d.featureTags.includes(id) ? d.featureTags.filter((x) => x !== id) : [...d.featureTags, id] }));
      // No photo mark on the chips (owner, 2026-10-29): the photos still
      // confirm a sea view or a pool, the chip just does not say so.
      const chip = (def: (typeof eligible)[number]) => {
        const on = draft.featureTags.includes(def.id);
        return (
          <button
            key={def.id}
            type="button"
            aria-pressed={on}
            onClick={() => toggleTag(def.id)}
            className={opt(on)}
          >
            {(() => {
              const Icon = AMENITY_ICON[def.id];
              return Icon ? <Icon className={`h-3.5 w-3.5 shrink-0 ${on ? "text-gray-900" : "text-gray-400"}`} aria-hidden /> : null;
            })()}
            {labelOf(def)}
          </button>
        );
      };
      const q = foldKeepIndex(tagQuery.trim());
      const matches = q ? eligible.filter((d) => foldKeepIndex(Object.values(d.labels).join(" ")).includes(q)) : null;
      const rowVisible = isMobile ? ROW_VISIBLE_PHONE : ROW_VISIBLE;
      const rows = AMENITY_ROWS.map((r) => {
        // The Soviet-era series are blocks of flats, not houses.
        if (r === "series" && kindNow === "house") return null;
        const tags = eligible.filter((d) => amenityRow(d.id, d.category) === r).sort((a, b) => order.get(a.id)! - order.get(b.id)!);
        if (!tags.length) return null;
        const head = new Set(tags.slice(0, rowVisible).map((d) => d.id));
        const isOpen = openRows.has(r);
        // A picked chip always shows, folded row or not.
        const shown = isOpen ? tags : tags.filter((d) => head.has(d.id) || draft.featureTags.includes(d.id));
        const hidden = tags.length - shown.length;
        const flip = () => setOpenRows((cur) => {
          const next = new Set(cur);
          if (next.has(r)) next.delete(r); else next.add(r);
          return next;
        });
        // The rows inside "İmkanlar" carry no icon: their chips do.
        return row(`amen_${r}`, t(`listings.filter.amen_row.${r}` as TranslationKey), (
          <div className="flex flex-wrap gap-2">
            {shown.map(chip)}
            {hidden > 0 && (
              <button type="button" onClick={flip} aria-expanded={false} className={moreWord}>
                {t("listings.filter.row_more").replace("{n}", String(hidden))}
              </button>
            )}
            {isOpen && tags.length > rowVisible && (
              <button type="button" onClick={flip} aria-expanded className={moreWord}>
                {t("listings.filter.row_less")}
              </button>
            )}
          </div>
        ));
      });
      return (
        <>
          <div>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden />
              <input
                type="text"
                value={tagQuery}
                onChange={(e) => { if (!tagQuery && e.target.value) trackEvent("listings.amenity_search"); setTagQuery(e.target.value); }}
                placeholder={t("listings.filter.amenities_search")}
                aria-label={t("listings.filter.amenities_search")}
                className={`${field} pl-9 pr-8`}
              />
              {tagQuery && (
                <button type="button" onClick={() => setTagQuery("")} aria-label={t("listings.filter.reset")} className="absolute right-1.5 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded text-gray-400 hover:text-gray-700">
                  <X className="h-4 w-4" aria-hidden />
                </button>
              )}
            </div>
          </div>
          {matches ? (
            matches.length ? <div className="flex flex-wrap gap-2">{matches.map(chip)}</div>
              : <p className="text-sm text-gray-500">{t("common.na")}</p>
          ) : rows}
        </>
      );
    })();
    // Not in alerts (owner, 2026-10-22): new listings get their photo
    // tags later than an alert looks back. Homes only: an office's photos
    // are not read for rooms.
    const photoHasRow = !forAlert && !separate && row("photo_has", t("listings.filter.photo_has"), (
      <div className="flex flex-wrap gap-2">
        {PHOTO_HAS.map((value) => {
          const on = draft.photoHas.includes(value);
          return (
            <button
              key={value}
              type="button"
              aria-pressed={on}
              onClick={() => set("photoHas", on ? draft.photoHas.filter((x) => x !== value) : [...draft.photoHas, value])}
              className={opt(on)}
            >
              {t(`listings.filter.photo_has.${value}`)}
            </button>
          );
        })}
      </div>
    ));
    // Land: no amenities (owner, 2026-10-12: "most of the filters should
    // just disappear"); a shop or a garage has none either.
    const featuresVisible = kindNow !== "land" && !separate;
    const features = amenityRows;
    // What the photos say: the vision model's read of the repair, and
    // what the gallery shows.
    const photos = (
      <>
        {/* A shop or a garage is never scored, so it would only empty the page. */}
        {showPhotoTier && row("photo_tier", t("listings.filter.photo_tier.short"), photoTiers)}
        {photoHasRow}
      </>
    );
    const photosVisible = showPhotoTier || !!photoHasRow;

    // 3. Sənəd və ödəniş (owner, 2026-10-29): the mortgage, the
    //    take-over ("hazır ipoteka"), the area on the deed. The deed
    //    itself is a main filter.
    const fullDocs = !landOnly && !separate;
    const toggleIn = (key: "mortgageYears", v: string) =>
      setDraft((d) => ({ ...d, [key]: d[key].includes(v) ? d[key].filter((x) => x !== v) : [...d[key], v] }));
    const small = (text: string) => <p className="mb-1.5 text-xs text-gray-500">{text}</p>;
    const rateBox = (key: "mortgageRateMin" | "mortgageRateMax") => unitBox(
      <input
        type="text"
        inputMode="decimal"
        value={draft[key]}
        onChange={(e) => set(key, rateInput(e.target.value))}
        placeholder={key === "mortgageRateMin" ? "4" : "12"}
        aria-label={`${t("listings.filter.mortgage_rate")}, ${t(key === "mortgageRateMin" ? "listings.filter.price.min" : "listings.filter.price.max")}`}
        className={`${draft[key] ? fieldSet : field} pr-7`}
      />,
      "%",
    );
    const docs = (
      <>
        {/* One question (2026-10-29): the text's "a mortgage is possible"
            decides, and the site's tick when it says nothing. */}
        {fullDocs && row("mortgage", t("listings.fact.mortgage"), (
          <div>
            {yesNo(draft.hasMortgage, [t("listings.filter.yesno.yes"), t("listings.filter.yesno.no")], (v) => set("hasMortgage", v), t("listings.fact.mortgage"))}
            {factsShown && <p className="mt-1.5 text-xs leading-snug text-gray-500">{t("listings.fact.merged.hint")}</p>}
          </div>
        ))}
        {/* A flat sold with its loan: the buyer pays the seller's share
            now and continues the bank payments (feedback ticket 9,
            2026-10-14). */}
        {fullDocs && row("takeover", t("listings.filter.mortgage_takeover"), (
          <div className="space-y-3">
            <button
              type="button"
              aria-pressed={draft.mortgageKind === "takeover"}
              onClick={() => setDraft((d) => {
                const next = d.mortgageKind === "takeover" ? "" : "takeover";
                return { ...d, mortgageKind: next, mortgageRateMin: next ? d.mortgageRateMin : "", mortgageRateMax: next ? d.mortgageRateMax : "", mortgageTermsKnown: next ? d.mortgageTermsKnown : "", mortgageYears: next ? d.mortgageYears : [] };
              })}
              className={`${opt(draft.mortgageKind === "takeover")} w-full sm:w-auto`}
            >
              {t("listings.filter.mortgage_takeover.btn")}
            </button>
            {draft.mortgageKind === "takeover" && (
              <div className="grid gap-4 rounded-xl border border-gray-200 p-3 sm:grid-cols-2 sm:p-4">
                <div>
                  {small(t("listings.filter.max_initial"))}
                  {unitBox(
                    <input
                      type="text"
                      inputMode="numeric"
                      value={draft.maxInitial.replace(/\B(?=(\d{3})+(?!\d))/g, " ")}
                      onChange={(e) => set("maxInitial", e.target.value.replace(/\D/g, "").replace(/^0+(?=\d)/, "").slice(0, 7))}
                      placeholder="120 000"
                      aria-label={t("listings.filter.max_initial")}
                      className={`${draft.maxInitial ? fieldSet : field} pr-12`}
                    />,
                    "AZN",
                  )}
                </div>
                <div>
                  {/* Whether the text states the terms at all: both ways,
                      and the choice rides into alerts. */}
                  {small(t("listings.filter.mortgage_terms"))}
                  {yesNo(draft.mortgageTermsKnown, yesNoLabels, (v) => set("mortgageTermsKnown", v), t("listings.filter.mortgage_terms"))}
                </div>
                <div>
                  {/* The loan's rate as a range (owner, 2026-10-16: the pills
                      offered rates the list did not have any more). */}
                  {small(t("listings.filter.mortgage_rate"))}
                  {pair(rateBox("mortgageRateMin"), rateBox("mortgageRateMax"))}
                </div>
                <div>
                  {/* The time left on the loan, in bands (owner, 2026-10-16). */}
                  {small(t("listings.filter.mortgage_years"))}
                  <div className="flex flex-wrap gap-2">
                    {(["le5", "6-10", "11-15", "gt15"] as const).map((b) => (
                      <button key={b} type="button" aria-pressed={draft.mortgageYears.includes(b)} onClick={() => toggleIn("mortgageYears", b)} className={opt(draft.mortgageYears.includes(b))}>
                        {t(`listings.filter.mortgage_years.${b}` as TranslationKey)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
        {/* The deed records less than the listed area (ticket 23). A
            home's valuation reads it; a shop's or a garage's has none. */}
        {fullDocs && row("deed_gap", t("listings.filter.deed_gap"), yesNo(
          draft.deedGap,
          [t("listings.filter.deed_gap.yes"), t("listings.filter.deed_gap.no")],
          (v) => set("deedGap", v),
          t("listings.filter.deed_gap"),
        ))}
      </>
    );

    // 4. Qiymət rəyi və elan: the model's verdict and the listing itself.
    // A plot, a shop and a garage get no verdict and no yield.
    const tiersShown = kindNow !== "land" && !separate;
    const viewedShown = !forAlert && !offers && (viewedCount > 0 || !!draft.viewed);
    // "Listed since" (owner, 2026-10-16): markers for today, yesterday
    // and the last 7 or 30 days, then any range by date. Days are the
    // browser's calendar days (Baku for our people; the API reads them
    // as Baku days too).
    const dayStr = (d: Date) => {
      const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, "0"), dd = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${dd}`;
    };
    const daysAgo = (n: number) => { const d = new Date(); d.setDate(d.getDate() - n); return dayStr(d); };
    const listedMarkers: { id: "today" | "yesterday" | "7d" | "30d"; after: string; before: string }[] = [
      { id: "today", after: daysAgo(0), before: "" },
      { id: "yesterday", after: daysAgo(1), before: daysAgo(1) },
      { id: "7d", after: daysAgo(6), before: "" },
      { id: "30d", after: daysAgo(29), before: "" },
    ];
    const time = (
      <div className="space-y-3">
        <div className="flex flex-wrap gap-2">
          {listedMarkers.map((m) => {
            const on = draft.listedAfter === m.after && draft.listedBefore === m.before;
            return (
              <button
                key={m.id}
                type="button"
                aria-pressed={on}
                onClick={() => setDraft((d) => ({ ...d, listedAfter: on ? "" : m.after, listedBefore: on ? "" : m.before }))}
                className={opt(on)}
              >
                {t(`listings.filter.listed.${m.id}` as TranslationKey)}
              </button>
            );
          })}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className="mb-1 block text-xs text-gray-500">{t("listings.filter.listed.from")}</span>
            <input type="date" value={draft.listedAfter} max={draft.listedBefore || undefined} onChange={(e) => set("listedAfter", e.target.value)} className={field} />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs text-gray-500">{t("listings.filter.listed.to")}</span>
            <input type="date" value={draft.listedBefore} min={draft.listedAfter || undefined} onChange={(e) => set("listedBefore", e.target.value)} className={field} />
          </label>
        </div>
      </div>
    );
    // The sites no longer fit a row of pills (owner, 2026-10-17): one
    // row says what is picked and opens the sites; most people never
    // filter by site.
    const sourcesOn = draft.sources.length > 0;
    const sources = (
      <div>
        <button
          type="button"
          aria-expanded={sourcesOpen}
          aria-controls="filter-sources"
          onClick={() => setSourcesOpen((o) => !o)}
          className={`flex h-9 w-full items-center justify-between gap-2 rounded-lg border px-3 text-sm font-medium transition-colors ${
            sourcesOn ? SELECTED : UNSELECTED
          }`}
        >
          <span className="truncate">{sourceSummary(draft.sources, LISTING_SOURCES, t("listings.filter.source.all"))}</span>
          <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${sourcesOn ? "text-gray-900" : "text-gray-400"} ${sourcesOpen ? "rotate-180" : ""}`} aria-hidden="true" />
        </button>
        {sourcesOpen && (
          <div id="filter-sources" className="mt-2 flex flex-wrap gap-2">
            {LISTING_SOURCES.map(({ id, label }) => {
              const on = draft.sources.includes(id);
              return (
                <button
                  key={id}
                  type="button"
                  aria-pressed={on}
                  onClick={() => set("sources", on ? draft.sources.filter((x) => x !== id) : [...draft.sources, id])}
                  className={opt(on)}
                >
                  {label}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
    const review = (
      <>
        {tiersShown && row("deal_tier", t("listings.filter.deal_tier"), (
          <div className="flex flex-wrap gap-2">
            {DEAL_TIERS.map(({ value, key }) => (
              <button key={value} type="button" aria-pressed={draft.dealTiers.includes(value)} onClick={() => toggleDealTier(value)} className={opt(draft.dealTiers.includes(value))}>
                {t(key)}
              </button>
            ))}
          </div>
        ))}
        {/* Rental yield floor (feedback ticket 34, 2026-10-18: "make
            price to rent more accessible"): the gross yield the index
            projects for a sale flat (listing_index.gross_yield_pct);
            rentals carry no yield. */}
        {tiersShown && draft.leased !== "true" && row("yield", t("listings.filter.min_yield"), (
          <div className="flex flex-wrap gap-2">
            {["5", "7", "9", "12"].map((v) => (
              <button key={v} type="button" aria-pressed={draft.minYield === v} onClick={() => set("minYield", draft.minYield === v ? "" : v)} className={opt(draft.minYield === v)}>
                {`≥ ${v}%`}
              </button>
            ))}
          </div>
        ))}
      </>
    );
    const reviewVisible = tiersShown;
    // Whether you saw it (the date is `time` above): each its own topic,
    // so the title says what it is and the control sits right under it.
    const viewed = (
      <div>
        {segmented(
          draft.viewed === "hide" || draft.viewed === "only" ? draft.viewed : "",
          [
            { value: "", label: t("listings.filter.any") },
            { value: "hide", label: t("viewed.filter.hide") },
            { value: "only", label: t("viewed.filter.pill.only") },
          ],
          (v) => set("viewed", v),
          t("viewed.filter"),
        )}
        <p className="mt-1.5 text-xs text-gray-500">{t("viewed.filter.count").replace("{n}", String(viewedCount))}</p>
      </div>
    );

    // The building group's name follows the kind: a flat's building, a
    // house, a plot, a shop or a garage.
    const homeTitle = t(
      kindNow === "house" ? "listings.filter.group.home.house"
        : kindNow === "land" ? "listings.filter.group.home.land"
          : kindNow === "apartment" ? "listings.filter.group.building"
            : "listings.filter.group.home.other",
    );
    // The topics (SECTIONS' order); one with nothing to offer for this
    // kind or page is left out.
    const shown: Record<SectionId, { title: string; icon: LucideIcon; body: React.ReactNode } | null> = {
      docs: fullDocs ? { title: t("listings.filter.group.docs"), icon: ICON.docs, body: docs } : null,
      features: featuresVisible ? { title: t("listings.filter.group.features"), icon: ICON.features, body: features } : null,
      source: !offers ? { title: t("listings.filter.group.source"), icon: ICON.source, body: sources } : null,
      seller: !offers ? { title: t("listings.filter.group.seller"), icon: ICON.seller, body: seller } : null,
      condition: topicOn("condition") ? { title: t("listings.filter.group.condition"), icon: ICON.condition, body: factBlock("condition") } : null,
      rent_terms: topicOn("rent_terms") ? { title: t("listings.filter.group.rent_terms"), icon: ICON.rentTerms, body: factBlock("rent_terms") } : null,
      review: reviewVisible ? { title: t("listings.filter.group.price_review"), icon: ICON.review, body: review } : null,
      building: buildingVisible ? { title: homeTitle, icon: ICON.building, body: building } : null,
      photos: photosVisible ? { title: t("listings.filter.group.photos"), icon: ICON.photos, body: photos } : null,
      // An alert always sends what is new: no listed date there.
      listed: !forAlert ? { title: t("listings.filter.listed.title"), icon: ICON.listing, body: time } : null,
      viewed: viewedShown ? { title: t("listings.filter.viewed.title"), icon: ICON.viewed, body: viewed } : null,
    };
    const more = SECTIONS.flatMap((id) => {
      const g = shown[id];
      return g ? [{ id, ...g }] : [];
    });
    // The "read from the text" note, once: at the top of the first open
    // topic that is all read from the text.
    const noteIn = more.find((g) => (g.id === "condition" || g.id === "rent_terms") && openSections.has(g.id))?.id;
    return { lead, essentials, more, noteIn, note: sourceNote };
  }

  if (!render || typeof document === "undefined") return null;
  const reduce = prefersReducedMotion();
  const phone = isPhoneWidth();

  const { lead, essentials, more, noteIn, note } = renderContent();

  // REAL portal to document.body: rendered inline, `fixed` breaks
  // inside ancestors with backdrop-filter/transform (the /map frosted
  // island) - the sheet re-anchored there and sat buried under the
  // mobile tab bar. Same fix as SortPicker / the filter-bar sheets.
  return createPortal(
    <div className="fixed inset-0 z-[1200] flex items-end sm:items-center justify-center">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        style={{ opacity: entered ? 1 : 0, transition: `opacity ${PANEL_MS}ms ease-out` }}
        onClick={closeSheet}
      />
      {/* Phones: the whole screen (the old 92vh sheet left a strip of
          dimmed page above a long scroll); desktop: a centred modal wide
          enough for a label column beside the choices. */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={t("listings.filter.all")}
        style={{
          // Phones: the panel slides up from below; desktop: the modal
          // grows in softly. With Reduce Motion both cross-fade in place.
          transform: dragging || entered
            ? (offset ? `translateY(${offset}px)` : "none")
            : reduce ? "none" : phone ? "translateY(100%)" : "scale(0.97)",
          opacity: entered ? 1 : reduce || !phone ? 0 : 1,
          transition: dragging ? "none" : `transform ${PANEL_MS}ms cubic-bezier(0.22, 1, 0.36, 1), opacity ${PANEL_MS}ms ease-out`,
        }}
        className="relative z-10 flex h-[100dvh] w-full flex-col bg-white shadow-2xl will-change-transform sm:h-auto sm:max-h-[92vh] sm:max-w-3xl sm:rounded-2xl sm:border sm:border-gray-200"
      >
        <div
          {...handleProps}
          className="flex items-center justify-between gap-3 border-b border-gray-200 px-5 pb-3 pt-[calc(0.75rem+env(safe-area-inset-top))] touch-none sm:py-4"
        >
          {/* The phone's sheet over the list is "Filtrlər"; on a desktop
              it opens from "Bütün filtrlər" and says so. */}
          <h2 className="text-lg font-bold text-gray-900">
            <span className="sm:hidden">{t("listings.filter.sheet_title")}</span>
            <span className="hidden sm:inline">{t("listings.filter.all")}</span>
          </h2>
          <div className="flex items-center gap-1.5">
            {headerExtras && (
              // Not part of the sheet's drag handle: a tap here is a tap.
              <div className="flex items-center gap-1.5 touch-auto" onPointerDown={(e) => e.stopPropagation()}>
                {headerExtras}
              </div>
            )}
            <button
              type="button"
              onClick={closeSheet}
              aria-label={t("listings.filter.close")}
              className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div ref={scrollRef} className="relative flex-1 overflow-y-auto px-5 py-5">
          <div className="space-y-4">
            {lead}
            {essentials}
          </div>
          {/* One row per topic that opens in place; the count says how
              many choices it holds. */}
          {more.length > 0 && <div className="mt-5 divide-y divide-gray-200 border-t border-gray-200">
            {more.map((g) => {
              const on = openSections.has(g.id);
              const n = picks[g.id];
              return (
                <div key={g.id}>
                  <h3>
                    <button
                      type="button"
                      data-section={g.id}
                      aria-expanded={on}
                      aria-controls={`filters-sec-${g.id}`}
                      onClick={() => toggleSection(g.id)}
                      className="group flex w-full items-center gap-2.5 py-4 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                    >
                      <g.icon className="h-4 w-4 shrink-0 text-gray-500" aria-hidden />
                      <span className="min-w-0 flex-1 truncate text-[15px] font-semibold text-gray-900">{g.title}</span>
                      {n > 0 && (
                        <>
                          <span aria-hidden className={`flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full px-1 text-xs font-bold tabular-nums ${FILTER_COUNT}`}>
                            {n}
                          </span>
                          <span className="sr-only">{t("listings.filter.section.picked").replace("{n}", String(n))}</span>
                        </>
                      )}
                      <ChevronDown className={`h-5 w-5 shrink-0 text-gray-400 transition-transform group-hover:text-gray-600 ${on ? "rotate-180" : ""}`} aria-hidden />
                    </button>
                  </h3>
                  {on && (
                    <div id={`filters-sec-${g.id}`} className="space-y-4 pb-5">
                      {noteIn === g.id && note}
                      {g.body}
                    </div>
                  )}
                </div>
              );
            })}
          </div>}
        </div>

        <div className="space-y-2.5 border-t border-gray-200 px-5 pb-[calc(0.75rem+env(safe-area-inset-bottom))] pt-3 sm:pb-3">
          {footerExtras && <div className="flex items-center gap-2">{footerExtras}</div>}
          {/* The bottom bar (owner, 2026-10-29): "Sıfırlayın" on the left,
              then the alert (the bell alone on a phone) and the live count,
              which IS the button ("N elanı göstərin"). The reset is off
              while nothing is set, so it never reads as a dead control. */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={reset}
              disabled={totalActive === 0}
              className="shrink-0 whitespace-nowrap rounded-xl border border-gray-200 px-3.5 py-2.5 text-sm font-semibold text-gray-700 transition-colors hover:border-gray-300 hover:text-gray-900 disabled:pointer-events-none disabled:text-gray-300"
            >
              {t("listings.filter.reset")}
            </button>
            <div className="flex min-w-0 flex-1 items-center justify-end gap-2">
              {!forAlert && !offers && <CreateAlertButton searchParams={draftQuery} tall />}
              <button
                type="button"
                onClick={runSearch}
                className="flex min-w-0 flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold tabular-nums text-white transition-colors hover:bg-indigo-700 active:bg-indigo-800 sm:flex-none sm:px-6"
              >
                {forAlert ? t("listings.filter.apply") : showLabel(t, locale, matchCount)}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
